import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  IDispositivoAlarma,
  IEventoGenerico,
  IGeoJSONPoint,
  IReporteBase,
  IUbicacionBase,
} from 'modelos/src';
import * as turf from '@turf/turf';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { ultimaComunicacionAlarma } from '../../../../../auxiliares/comunicadores-alarma.util';
import {
  FILTROS_ALARMAS_VACIOS,
  FiltrosAlarmasModalComponent,
  IAlarmasFiltros,
} from '../filtros-alarmas-modal/filtros-alarmas-modal.component';

interface IDispositivoAlarmaExtendido extends IDispositivoAlarma {
  eventosAbiertos?: IEventoGenerico[];
  eventosTecnicosAbiertos?: IEventoGenerico[];
}

/**
 * Estados que se pueden filtrar. Una alarma puede cumplir varios a la vez.
 *
 * Material Symbols, no emoji: el resto del panel (capas, buscador, acciones)
 * usa la misma familia de iconos y hereda el color del tema.
 */
const ESTADOS_ALARMA: { nombre: string; icono: string }[] = [
  { nombre: 'Reportando', icono: 'cell_tower' },
  { nombre: 'Sin reporte', icono: 'sensors_off' },
  { nombre: 'Con eventos abiertos', icono: 'report' },
  { nombre: 'Con eventos técnicos', icono: 'build' },
  { nombre: 'Con cámara', icono: 'videocam' },
];

/**
 * Panel flotante del mapa de alarmas.
 *
 * Misma forma que `flotante-luminarias`: panel compacto con contador en el
 * header, un buscador libre, los filtros finos en un modal aparte
 * (`filtros-alarmas-modal`), las capas como pastillas de iconos y los estados
 * como chips con conteo.
 */
@Component({
  selector: 'app-flotante-alarmas',
  templateUrl: './flotante-alarmas.component.html',
  styleUrl: './flotante-alarmas.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FlotanteAlarmasComponent implements OnInit {
  helper = inject(HelperService);
  private cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);

  private _alarmas?: IDispositivoAlarmaExtendido[] = [];
  @Input() set alarmas(value: IDispositivoAlarmaExtendido[] | undefined) {
    this._alarmas = value;
    this.filtrarAlarmas();
    this.cdr.markForCheck();
  }
  get alarmas(): IDispositivoAlarmaExtendido[] | undefined {
    return this._alarmas;
  }

  @Input() vehiculosSeguridadPrivada?: IReporteBase<'Tracker 4G'>[] = [];
  /** Zonas geográficas (las carga el mapa para dibujar sus polígonos) */
  @Input() zonas?: IUbicacionBase<'Alarmas'>[] = [];

  @Output() alarmaChange = new EventEmitter<IDispositivoAlarma[]>();
  @Output() clusterChange = new EventEmitter<boolean>();
  @Output() seguridadPrivadaChange = new EventEmitter<boolean>();
  @Output() poligonoZonasChange = new EventEmitter<boolean>();
  @Output() textoZonasChange = new EventEmitter<boolean>();

  public expansionPanelExpanded = true;

  // ── Estado de las capas ───────────────────────────────────────────────────
  public clusterEnabled = false;
  public seguridadPrivadaEnabled = true;
  public poligonoZonasEnabled = true;
  public textoZonasEnabled = true;

  // ── Filtros ───────────────────────────────────────────────────────────────
  /** Buscador libre del panel (dirección + nombre) */
  public textoBusqueda = '';
  /** Filtros finos, editados en el modal */
  public filtros: IAlarmasFiltros = { ...FILTROS_ALARMAS_VACIOS };
  /**
   * Estados visibles. Arrancan todos marcados; se ve lo que matchea alguno de
   * los marcados, igual que en luminarias.
   *
   * Ojo con una diferencia de fondo: en luminarias cada luminaria tiene UN
   * estado, así que los buckets son disjuntos. Acá los cinco son atributos que
   * se superponen (una alarma puede estar reportando Y tener cámara). Con la
   * regla de inclusión eso implica que destildar SOLO "Con cámara" no saca del
   * mapa a una alarma con cámara que además está reportando: sigue entrando por
   * el chip de comunicación.
   *
   * Se probó la regla inversa (ocultar lo que matchea algún destildado) y es
   * peor: 'Reportando' y 'Sin reporte' son complementarios, así que dejar un
   * solo chip tildado destildaba los dos y ocultaba TODAS las alarmas.
   */
  public filtrosEstado: string[] = ESTADOS_ALARMA.map((e) => e.nombre);

  public estados: { nombre: string; cant: number; icono: string }[] = [];
  public alarmasFiltradas: IDispositivoAlarmaExtendido[] = [];

  get isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  @HostListener('window:resize')
  onResize() {
    const wasMobile = !this.expansionPanelExpanded;
    const isMobileNow = window.innerWidth <= 768;
    if (wasMobile !== isMobileNow) {
      this.expansionPanelExpanded = !isMobileNow;
    }
  }

  // ── Getters de estado de los filtros ──────────────────────────────────────

  public get hayFiltrosModalActivos(): boolean {
    return (
      this.filtros.direccion.length > 0 ||
      this.filtros.nombre.length > 0 ||
      this.filtros.clientes.length > 0 ||
      this.filtros.zonas.length > 0
    );
  }

  public get hayBusquedaOFiltrosModal(): boolean {
    return this.textoBusqueda.trim().length > 0 || this.hayFiltrosModalActivos;
  }

  public get hayEstadosFiltrados(): boolean {
    return this.filtrosEstado.length !== ESTADOS_ALARMA.length;
  }

  public get hayFiltrosActivos(): boolean {
    return this.hayBusquedaOFiltrosModal || this.hayEstadosFiltrados;
  }

  // ── Filtrado ──────────────────────────────────────────────────────────────

  private normalizar(texto: string): string {
    return (texto ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  }

  public filtrarAlarmas(): void {
    let resultado = this.alarmas || [];

    // Buscador libre: dirección + nombre
    const termino = this.normalizar(this.textoBusqueda).trim();
    if (termino.length >= 2) {
      resultado = resultado.filter(
        (a) =>
          this.normalizar(a.domicilio?.direccion).includes(termino) ||
          this.normalizar(a.nombre).includes(termino),
      );
    }

    if (this.filtros.direccion.length >= 1) {
      const t = this.normalizar(this.filtros.direccion);
      resultado = resultado.filter((a) =>
        this.normalizar(a.domicilio?.direccion).includes(t),
      );
    }

    if (this.filtros.nombre.length >= 1) {
      const t = this.normalizar(this.filtros.nombre);
      resultado = resultado.filter((a) =>
        this.normalizar(a.nombre).includes(t),
      );
    }

    if (this.filtros.clientes.length > 0) {
      resultado = resultado.filter(
        (a) => a.idCliente && this.filtros.clientes.includes(a.idCliente),
      );
    }

    resultado = this.filtrarPorZonas(resultado);

    resultado = resultado.filter((a) => this.alarmaCumpleFiltro(a));

    this.alarmasFiltradas = resultado;
    this.recalcularEstados();
    this.alarmaChange.emit(resultado);
  }

  /**
   * Una alarma entra si el punto de su domicilio cae dentro de alguno de los
   * polígonos elegidos. Los polígonos se arman UNA vez por corrida, no por
   * alarma: son ~6.000 alarmas y esto corre en el hilo principal.
   */
  private filtrarPorZonas(
    lista: IDispositivoAlarmaExtendido[],
  ): IDispositivoAlarmaExtendido[] {
    const ids = this.filtros.zonas;
    if (!ids.length) return lista;

    const poligonos = (this.zonas ?? [])
      .filter(
        (z) =>
          z._id &&
          ids.includes(z._id) &&
          (z.geojson?.type === 'Polygon' || z.geojson?.type === 'MultiPolygon'),
      )
      .map((z) => z.geojson as GeoJSON.Polygon | GeoJSON.MultiPolygon);

    if (!poligonos.length) return lista;

    return lista.filter((a) => {
      const coords = (a.domicilio?.geojson as IGeoJSONPoint)?.coordinates;
      if (!coords || coords.length < 2) return false;
      // [0,0] es basura conocida en producción (24 domicilios): no cae en
      // ninguna zona real y no tiene sentido evaluarlo.
      if (!coords[0] && !coords[1]) return false;
      const punto = turf.point([coords[0], coords[1]]);
      return poligonos.some((poly) => turf.booleanPointInPolygon(punto, poly));
    });
  }

  /** Se ve si matchea alguno de los estados tildados (ver `filtrosEstado`) */
  public alarmaCumpleFiltro(alarma: IDispositivoAlarmaExtendido): boolean {
    return this.filtrosEstado.some((estado) =>
      this.cumpleEstado(alarma, estado),
    );
  }

  private cumpleEstado(
    alarma: IDispositivoAlarmaExtendido,
    estado: string,
  ): boolean {
    switch (estado) {
      case 'Con eventos abiertos':
        return alarma.eventosAbiertos?.length > 0;
      case 'Sin reporte':
        return !this.tieneComunicacionReciente(alarma);
      case 'Reportando':
        return this.tieneComunicacionReciente(alarma);
      case 'Con eventos técnicos':
        return alarma.eventosTecnicosAbiertos?.length > 0;
      // idsCamaras en vez del populate `camaras`: el mapa ya no lo trae (payload)
      case 'Con cámara':
        return alarma.idsCamaras?.length > 0;
      default:
        return false;
    }
  }

  /**
   * Conteo de cada estado sobre las alarmas que pasan los DEMÁS filtros. Se
   * calcula una vez por filtrado y no por chip: el template se evalúa en cada
   * ciclo de detección y esto recorre las ~6.000 alarmas.
   */
  private recalcularEstados(): void {
    const base = this.baseParaConteos();
    this.estados = ESTADOS_ALARMA.map((estado) => ({
      ...estado,
      cant: base.filter((a) => this.cumpleEstado(a, estado.nombre)).length,
    }));
  }

  /** Las alarmas que pasan todos los filtros MENOS el de estados */
  private baseParaConteos(): IDispositivoAlarmaExtendido[] {
    let resultado = this.alarmas || [];
    const termino = this.normalizar(this.textoBusqueda).trim();
    if (termino.length >= 2) {
      resultado = resultado.filter(
        (a) =>
          this.normalizar(a.domicilio?.direccion).includes(termino) ||
          this.normalizar(a.nombre).includes(termino),
      );
    }
    if (this.filtros.direccion.length >= 1) {
      const t = this.normalizar(this.filtros.direccion);
      resultado = resultado.filter((a) =>
        this.normalizar(a.domicilio?.direccion).includes(t),
      );
    }
    if (this.filtros.nombre.length >= 1) {
      const t = this.normalizar(this.filtros.nombre);
      resultado = resultado.filter((a) =>
        this.normalizar(a.nombre).includes(t),
      );
    }
    if (this.filtros.clientes.length > 0) {
      resultado = resultado.filter(
        (a) => a.idCliente && this.filtros.clientes.includes(a.idCliente),
      );
    }
    return this.filtrarPorZonas(resultado);
  }

  private tieneComunicacionReciente(
    alarma: IDispositivoAlarmaExtendido,
  ): boolean {
    const ultimaComunicacion = ultimaComunicacionAlarma(alarma);
    if (!ultimaComunicacion || !alarma.frecReporte) return false;

    const fechaUltima = new Date(ultimaComunicacion).getTime();
    const ahora = Date.now();
    const frecEnMs = alarma.frecReporte * 60 * 1000;

    return ahora - fechaUltima <= 2 * frecEnMs;
  }

  // ── Acciones del panel ────────────────────────────────────────────────────

  public onBusquedaChange(): void {
    this.filtrarAlarmas();
  }

  public limpiarBusqueda(): void {
    this.textoBusqueda = '';
    this.filtrarAlarmas();
  }

  public toggleEstado(estado: string): void {
    const idx = this.filtrosEstado.indexOf(estado);
    if (idx > -1) this.filtrosEstado.splice(idx, 1);
    else this.filtrosEstado.push(estado);
    this.filtrarAlarmas();
  }

  /** Limpia sólo buscador + filtros del modal (no toca estados) */
  public limpiarSoloFiltros(): void {
    this.textoBusqueda = '';
    this.filtros = { ...FILTROS_ALARMAS_VACIOS };
    this.filtrarAlarmas();
  }

  /** Vuelve a marcar todos los estados (no oculta nada) */
  public limpiarSoloEstados(): void {
    this.filtrosEstado = ESTADOS_ALARMA.map((e) => e.nombre);
    this.filtrarAlarmas();
  }

  public limpiarFiltros(): void {
    this.textoBusqueda = '';
    this.filtros = { ...FILTROS_ALARMAS_VACIOS };
    this.filtrosEstado = ESTADOS_ALARMA.map((e) => e.nombre);
    this.filtrarAlarmas();
  }

  private filtrosDialogRef?: MatDialogRef<FiltrosAlarmasModalComponent>;

  public abrirDialogFiltros(): void {
    // Una sola instancia: si ya está abierto, no se apila otro modal.
    if (this.filtrosDialogRef) return;

    const ref = this.dialog.open(FiltrosAlarmasModalComponent, {
      width: '400px',
      maxWidth: '92vw',
      maxHeight: '88vh',
      panelClass: 'filtros-alarmas-dialog',
      // No-modal: sin backdrop el mapa detrás queda usable (mover, zoom, click).
      hasBackdrop: false,
      // Se cierra sólo con la X: ni click afuera ni Escape.
      disableClose: true,
      data: {
        filtros: { ...this.filtros },
        zonas: this.zonas ?? [],
        onLiveChange: (filtros: IAlarmasFiltros) => {
          this.filtros = filtros;
          this.filtrarAlarmas();
          this.cdr.markForCheck();
        },
      },
    });
    this.filtrosDialogRef = ref;
    ref.afterClosed().subscribe((res: IAlarmasFiltros | undefined) => {
      this.filtrosDialogRef = undefined;
      if (res) {
        this.filtros = res;
        this.filtrarAlarmas();
        this.cdr.markForCheck();
      }
    });
  }

  // ── Capas ─────────────────────────────────────────────────────────────────

  public onToggleCluster(): void {
    this.clusterEnabled = !this.clusterEnabled;
    this.clusterChange.emit(this.clusterEnabled);
  }

  public onToggleSeguridadPrivada(): void {
    this.seguridadPrivadaEnabled = !this.seguridadPrivadaEnabled;
    this.seguridadPrivadaChange.emit(this.seguridadPrivadaEnabled);
  }

  public onTogglePoligonoZonas(): void {
    this.poligonoZonasEnabled = !this.poligonoZonasEnabled;
    this.poligonoZonasChange.emit(this.poligonoZonasEnabled);
  }

  public onToggleTextoZonas(): void {
    this.textoZonasEnabled = !this.textoZonasEnabled;
    this.textoZonasChange.emit(this.textoZonasEnabled);
  }

  ngOnInit(): void {
    this.expansionPanelExpanded = window.innerWidth > 768;
    this.filtrarAlarmas();
  }
}
